import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Copy, ExternalLink, KeyRound, Loader2, Mail, Palette, Plus, UserPlus, X, FlaskConical, Trash2 } from "lucide-react";

/* ----------------------- Organizations view -----------------------
 *
 * One-stop hub for the platform admin to:
 *   1) Create a new partner org (auto-generates 8-char invite code +
 *      provisions an org_admin user with a temp password the founder can
 *      copy-paste into a welcome email).
 *   2) See every existing org with its code + member count + plan.
 *   3) Click any org row to expand a brand editor (logo URL, primary +
 *      accent colors, display name, tagline, blurb) — saves to backend
 *      and is reflected on the public /api/org/brand/{code} surface.
 *
 * Goal: zero re-coding to onboard a new organization or re-skin their
 * caregiver experience.
 */
export default function OrganizationsView() {
    const [orgs, setOrgs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);
    const [creating, setCreating] = useState(false);
    const [lastCreated, setLastCreated] = useState(null);
    const [form, setForm] = useState({
        name: "", admin_email: "", admin_name: "", plan: "pilot", contact_email: "",
        brand: { display_name: "", tagline: "", primary_color: "", accent_color: "", logo_url: "", public_landing_blurb: "" },
    });

    const reload = async () => {
        try {
            const r = await api.get("/org/admin/list");
            setOrgs(r.data || []);
        } catch {
            toast.error("Couldn't load organizations.");
        }
    };

    useEffect(() => {
        let active = true;
        (async () => {
            await reload();
            if (active) setLoading(false);
        })();
        return () => { active = false; };
    }, []);

    const updateForm = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const updateBrand = (k, v) => setForm(f => ({ ...f, brand: { ...f.brand, [k]: v } }));

    const submitCreate = async (e) => {
        e?.preventDefault?.();
        if (!form.name.trim() || !form.admin_email.trim()) {
            toast("Org name and admin email are required.");
            return;
        }
        setCreating(true);
        try {
            const r = await api.post("/org/admin/create-with-admin", {
                name: form.name.trim(),
                plan: form.plan,
                contact_email: form.contact_email.trim(),
                admin_email: form.admin_email.trim(),
                admin_name: form.admin_name.trim(),
                brand: Object.fromEntries(Object.entries(form.brand).filter(([_, v]) => v && v.toString().trim())),
            });
            setLastCreated(r.data);
            setForm({
                name: "", admin_email: "", admin_name: "", plan: "pilot", contact_email: "",
                brand: { display_name: "", tagline: "", primary_color: "", accent_color: "", logo_url: "", public_landing_blurb: "" },
            });
            await reload();
            toast.success("Organization created.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't create the org.");
        } finally {
            setCreating(false);
        }
    };

    if (loading) {
        return <div className="text-[#4C5551] flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading organizations…</div>;
    }

    return (
        <section className="space-y-6" data-testid="admin-orgs-view">
            <header>
                <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight">Organizations</h1>
                <p className="mt-1 text-[#4C5551]">
                    Create a partner organization in one step: we generate an 8-character invite code, provision the org admin user, and let you co-brand the caregiver experience without re-deploying.
                </p>
                <div className="mt-3 flex gap-2 flex-wrap">
                    <a href="/org-intel" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-2xl text-xs font-semibold bg-[#1F2624] text-white hover:bg-teal-dark transition-colors" data-testid="admin-open-org-intel">
                        Open Intelligence Dashboard →
                    </a>
                    <a href="/org-admin" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-2xl text-xs font-semibold bg-white border border-cream-dark text-[#4C5551] hover:border-teal transition-colors" data-testid="admin-open-org-pilot">
                        Pilot admin (invites, branding, families)
                    </a>
                </div>
            </header>

            {/* Create form */}
            <article className="ml-card p-5 md:p-6 bg-gradient-to-br from-teal-soft/30 via-cream to-yellow-light/30 border border-teal-light" data-testid="admin-orgs-create-card">
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-2xl bg-teal text-white flex items-center justify-center"><Plus size={18} strokeWidth={1.7} /></div>
                    <div>
                        <h2 className="text-xl font-heading font-medium">Add an organization</h2>
                        <p className="text-sm text-[#4C5551]">Fill this in once. We&apos;ll create the org, code, and admin account — and hand back everything you need to send a welcome email.</p>
                    </div>
                </div>
                <form onSubmit={submitCreate} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-orgs-create-form">
                    <Field label="Organization name *" testid="org-name" required value={form.name} onChange={v => updateForm("name", v)} placeholder="Sunset Hospice" />
                    <Field label="Plan" testid="org-plan" value={form.plan} onChange={v => updateForm("plan", v)} placeholder="pilot" />
                    <Field label="Admin email *" testid="org-admin-email" required type="email" value={form.admin_email} onChange={v => updateForm("admin_email", v)} placeholder="director@sunset.example" />
                    <Field label="Admin name" testid="org-admin-name" value={form.admin_name} onChange={v => updateForm("admin_name", v)} placeholder="Pat Sunset" />
                    <Field label="Contact email (optional)" testid="org-contact-email" type="email" value={form.contact_email} onChange={v => updateForm("contact_email", v)} placeholder="intake@sunset.example" />
                    <div className="md:col-span-2 mt-2 mb-1 flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-teal-dark">
                        <Palette size={14} /> Co-brand pack (optional — can edit later)
                    </div>
                    <Field label="Display name (overrides org name)" testid="brand-display-name" value={form.brand.display_name} onChange={v => updateBrand("display_name", v)} placeholder="Sunset Hospice & Palliative" />
                    <Field label="Tagline" testid="brand-tagline" value={form.brand.tagline} onChange={v => updateBrand("tagline", v)} placeholder="Comfort, dignity, every day." />
                    <ColorField label="Primary color (hex)" testid="brand-primary-color" value={form.brand.primary_color} onChange={v => updateBrand("primary_color", v)} />
                    <ColorField label="Accent color (hex)" testid="brand-accent-color" value={form.brand.accent_color} onChange={v => updateBrand("accent_color", v)} />
                    <Field label="Logo URL (https://…)" testid="brand-logo-url" value={form.brand.logo_url} onChange={v => updateBrand("logo_url", v)} placeholder="https://example.com/logo.png" className="md:col-span-2" />
                    <Field label="Landing blurb (1–2 sentences)" testid="brand-blurb" value={form.brand.public_landing_blurb} onChange={v => updateBrand("public_landing_blurb", v)} placeholder="Welcome from your Sunset team. Use Memory Lane to walk this with us." className="md:col-span-2" />
                    <div className="md:col-span-2 flex items-center gap-3 mt-2">
                        <button
                            type="submit"
                            disabled={creating}
                            data-testid="admin-orgs-create-submit"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-teal-dark text-white text-sm font-medium hover:bg-teal disabled:opacity-60"
                        >
                            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create organization + admin
                        </button>
                        <p className="text-xs text-[#6E7A74]">Generates an 8-char code, provisions the org admin, and (if new) returns a temp password.</p>
                    </div>
                </form>
            </article>

            {/* Last-created handoff card — the bit the founder copies into a welcome email */}
            {lastCreated && <NewOrgHandoffCard data={lastCreated} onDismiss={() => setLastCreated(null)} />}

            {/* Org list */}
            <article className="ml-card p-0 bg-white border border-cream-dark overflow-hidden" data-testid="admin-orgs-list">
                <header className="px-5 md:px-6 py-4 border-b border-cream-dark bg-cream-light/60">
                    <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">Active organizations</p>
                    <h2 className="text-xl font-heading font-medium">{orgs.length} {orgs.length === 1 ? "organization" : "organizations"}</h2>
                </header>
                <ul className="divide-y divide-cream-dark">
                    {orgs.length === 0 && <li className="px-5 md:px-6 py-8 text-sm text-[#6E7A74]">No organizations yet. Use the form above to create your first partner.</li>}
                    {orgs.map(o => (
                        <OrgRow key={o.id} org={o} expanded={expanded === o.id} onToggle={() => setExpanded(e => e === o.id ? null : o.id)} onUpdated={reload} />
                    ))}
                </ul>
            </article>

            <NdaLeadsList />
        </section>
    );
}

function NdaLeadsList() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    const reload = async () => {
        try {
            const r = await api.get("/legal/nda-requests");
            setLeads(r.data || []);
        } catch { /* fail soft */ }
    };

    useEffect(() => {
        let active = true;
        (async () => {
            await reload();
            if (active) setLoading(false);
        })();
        return () => { active = false; };
    }, []);

    const setStatus = async (lead, next) => {
        // optimistic
        setLeads(ls => ls.map(l => l.id === lead.id ? { ...l, status: next } : l));
        try {
            await api.patch(`/legal/nda-requests/${lead.id}`, { status: next });
            toast.success(`Marked ${LEAD_STATUS_LABEL[next]}.`);
        } catch {
            toast.error("Couldn't update — refreshing.");
            await reload();
        }
    };

    const downloadCsv = async () => {
        try {
            // api instance carries the auth header
            const r = await api.get("/legal/nda-requests.csv", { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `memorylane-nda-leads-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("CSV downloaded.");
        } catch {
            toast.error("CSV export failed.");
        }
    };

    const visible = filter === "all" ? leads : leads.filter(l => (l.status || "new") === filter);
    const counts = leads.reduce((acc, l) => {
        const s = l.status || "new";
        acc[s] = (acc[s] || 0) + 1;
        return acc;
    }, {});

    return (
        <article className="ml-card p-0 bg-white border border-cream-dark overflow-hidden" data-testid="admin-orgs-leads">
            <header className="px-5 md:px-6 py-4 border-b border-cream-dark bg-yellow-light/40 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <p className="text-xs uppercase tracking-wider font-semibold text-yellow-dark">Leads</p>
                    <h2 className="text-xl font-heading font-medium">NDA download requests · {leads.length}</h2>
                    <p className="text-xs text-[#4C5551] mt-1">Each row is a prospect who gated themselves at the NDA download. Track them here so nothing falls through the cracks.</p>
                </div>
                <button
                    type="button"
                    onClick={downloadCsv}
                    data-testid="admin-orgs-leads-csv"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-2xl bg-teal-dark text-white text-xs font-medium hover:bg-teal"
                >
                    Export CSV
                </button>
            </header>

            {/* Status filter */}
            <div className="px-5 md:px-6 py-3 border-b border-cream-dark flex flex-wrap gap-1.5" data-testid="admin-orgs-leads-filter">
                {[["all", "All"], ["new", "New"], ["contacted", "Contacted"], ["pilot_started", "Pilot started"], ["dropped", "Dropped"]].map(([v, label]) => {
                    const n = v === "all" ? leads.length : (counts[v] || 0);
                    const active = filter === v;
                    return (
                        <button
                            key={v}
                            type="button"
                            onClick={() => setFilter(v)}
                            data-testid={`admin-orgs-leads-filter-${v}`}
                            className={`px-3 py-1 rounded-2xl text-xs font-medium border ${active ? "bg-teal-dark text-white border-teal-dark" : "bg-white border-cream-dark text-[#4C5551] hover:bg-cream-light"}`}
                        >
                            {label} · {n}
                        </button>
                    );
                })}
            </div>

            {loading ? (
                <div className="px-5 md:px-6 py-6 text-sm text-[#6E7A74] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
            ) : visible.length === 0 ? (
                <div className="px-5 md:px-6 py-8 text-sm text-[#6E7A74]">No leads in this filter.</div>
            ) : (
                <ul className="divide-y divide-cream-dark">
                    {visible.map(lead => (
                        <li key={lead.id} className="px-5 md:px-6 py-3" data-testid={`admin-orgs-lead-${lead.id}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <p className="font-medium text-[#1F2624]">
                                        {lead.name} <span className="text-[#6E7A74] text-sm">·</span> <span className="text-[#4C5551]">{lead.org_name}</span>
                                        {lead.is_personal_email && (
                                            <span className="ml-2 text-xs px-2 py-0.5 rounded-2xl bg-yellow-light text-yellow-dark border border-yellow">personal email</span>
                                        )}
                                        <LeadStatusPill status={lead.status || "new"} />
                                    </p>
                                    <p className="text-xs text-[#4C5551]">
                                        <a href={`mailto:${lead.email}?subject=Re: Memory Lane mutual NDA`} className="text-teal-dark underline">{lead.email}</a>
                                        {lead.role && <> · {lead.role}</>} · {lead.created_at?.slice(0, 16).replace("T", " ")}
                                    </p>
                                    {lead.message && <p className="text-sm text-[#1F2624] mt-1.5 px-3 py-2 rounded-2xl bg-cream-light">&ldquo;{lead.message}&rdquo;</p>}
                                </div>
                                <div className="flex flex-col items-end gap-1.5 shrink-0">
                                    <a href={`mailto:${lead.email}?subject=Re: Memory Lane mutual NDA — ${lead.org_name}`} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-2xl bg-teal-dark text-white hover:bg-teal">
                                        <Mail size={11} /> Reply
                                    </a>
                                    <select
                                        value={lead.status || "new"}
                                        onChange={e => setStatus(lead, e.target.value)}
                                        data-testid={`admin-orgs-lead-status-${lead.id}`}
                                        className="text-xs px-2.5 py-1 rounded-2xl bg-white border border-cream-dark text-[#1F2624]"
                                    >
                                        <option value="new">New</option>
                                        <option value="contacted">Contacted</option>
                                        <option value="pilot_started">Pilot started</option>
                                        <option value="dropped">Dropped</option>
                                    </select>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </article>
    );
}

const LEAD_STATUS_LABEL = {
    new: "New",
    contacted: "Contacted",
    pilot_started: "Pilot started",
    dropped: "Dropped",
};
const LEAD_STATUS_TONE = {
    new: "bg-yellow-light text-yellow-dark border-yellow",
    contacted: "bg-teal-soft text-teal-dark border-teal-light",
    pilot_started: "bg-sage-light text-sage-dark border-sage-soft",
    dropped: "bg-cream-dark/40 text-[#6E7A74] border-cream-dark",
};
function LeadStatusPill({ status }) {
    return (
        <span className={`ml-2 inline-flex items-center text-xs px-2 py-0.5 rounded-2xl border ${LEAD_STATUS_TONE[status] || LEAD_STATUS_TONE.new}`}>
            {LEAD_STATUS_LABEL[status] || status}
        </span>
    );
}

function Field({ label, testid, value, onChange, placeholder, type = "text", required, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">{label}</span>
            <input
                type={type}
                required={required}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={`admin-orgs-field-${testid}`}
                placeholder={placeholder}
                className="w-full px-3 py-2 rounded-2xl bg-white border border-cream-dark text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
            />
        </label>
    );
}

function ColorField({ label, testid, value, onChange }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">{label}</span>
            <div className="flex items-center gap-2">
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={`admin-orgs-field-${testid}`}
                    placeholder="#1D8A85"
                    className="flex-1 px-3 py-2 rounded-2xl bg-white border border-cream-dark text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-soft"
                />
                <input
                    type="color"
                    value={/^#[0-9a-fA-F]{6}$/.test(value || "") ? value : "#1D8A85"}
                    onChange={(e) => onChange(e.target.value)}
                    aria-label={`${label} swatch picker`}
                    className="w-10 h-10 rounded-xl border border-cream-dark cursor-pointer"
                />
            </div>
        </label>
    );
}

function NewOrgHandoffCard({ data, onDismiss }) {
    const code = data?.org?.code;
    const orgName = data?.org?.name;
    const email = data?.admin?.email;
    const temp = data?.admin?.temp_password;
    const wasCreated = data?.admin?.was_created;
    const wasReset = data?.admin?.was_password_reset;
    const blurb = data?.next_steps?.share_blurb || "";
    const emailSent = !!data?.email?.sent;
    const emailReason = data?.email?.reason;

    const welcomeBody = [
        `Welcome to Memory Lane, ${orgName}!`,
        "",
        `Here's everything your team needs:`,
        "",
        `• Invite code (share with your families): ${code}`,
        `• Org admin sign-in: ${typeof window !== "undefined" ? window.location.origin : ""}/login`,
        `• Admin email: ${email}`,
        wasCreated && temp ? `• Temporary password: ${temp}` : (wasReset && temp ? `• Temporary password (reset): ${temp}` : ""),
        "",
        `Families redeem the code at ${typeof window !== "undefined" ? window.location.origin : ""}/  (look for "I have an invite code"). Once they sign in, they'll be tagged to your organization automatically.`,
        "",
        "Let us know if you'd like a co-branded landing or any tweaks to the colors / logo — those live in your org admin dashboard.",
        "",
        "— Memory Lane Care Navigator",
    ].filter(Boolean).join("\n");

    const copyAll = async () => {
        try { await navigator.clipboard.writeText(welcomeBody); toast.success("Welcome email copied to clipboard."); }
        catch { toast("Copy failed — long-press and copy the text below."); }
    };

    return (
        <article className="ml-card p-5 md:p-6 bg-sage-light/80 border border-sage-soft" data-testid="admin-orgs-handoff">
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-sage text-white flex items-center justify-center"><Check size={18} strokeWidth={2} /></div>
                    <div>
                        <h3 className="text-lg font-heading font-medium">{orgName} is ready</h3>
                        <p className="text-sm text-[#4C5551]">Code: <span className="font-mono font-semibold text-sage-dark">{code}</span></p>
                    </div>
                </div>
                <button type="button" onClick={onDismiss} data-testid="admin-orgs-handoff-dismiss" className="text-[#6E7A74] hover:text-[#4C5551]"><X size={16} /></button>
            </div>
            {temp && (
                <div className="rounded-2xl bg-yellow-light border border-yellow p-3 mb-3 flex items-start gap-2" data-testid="admin-orgs-handoff-temp-password">
                    <KeyRound size={14} className="text-yellow-dark mt-0.5" />
                    <div className="text-sm">
                        <p className="text-[#1F2624] font-medium">
                            {wasCreated ? "Temp password for " : "Password reset — temp password for "}
                            {email}
                        </p>
                        <p className="font-mono text-yellow-dark">{temp}</p>
                        <p className="text-xs text-[#4C5551] mt-1">
                            {wasCreated
                                ? "Share this only with the org admin — they can change it on first sign-in."
                                : "This user already existed; their password has been reset. Share only with the org admin."}
                            {" "}Won&apos;t be shown again.
                        </p>
                    </div>
                </div>
            )}
            <p className="text-sm text-[#1F2624] mb-3">{blurb}</p>
            <div className="mb-3 text-xs inline-flex items-center gap-1.5 px-3 py-1 rounded-2xl bg-white border border-sage-soft text-sage-dark" data-testid="admin-orgs-handoff-email-status">
                {emailSent
                    ? <><Mail size={12} /> Welcome email sent to {email}</>
                    : <><Mail size={12} /> Welcome email not sent — {emailReason === "resend_not_configured" ? "set RESEND_API_KEY in backend/.env" : (emailReason || "see logs")}. Copy + paste below instead.</>}
            </div>
            <textarea
                readOnly
                value={welcomeBody}
                data-testid="admin-orgs-handoff-welcome"
                rows={9}
                className="w-full px-3 py-2 rounded-2xl border border-sage-soft bg-white font-mono text-xs text-[#1F2624] resize-none"
            />
            <button type="button" onClick={copyAll} data-testid="admin-orgs-handoff-copy" className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-sage text-white text-sm font-medium hover:bg-sage-dark">
                <Copy size={14} /> Copy welcome email
            </button>
        </article>
    );
}

function OrgRow({ org, expanded, onToggle, onUpdated }) {
    return (
        <li data-testid={`admin-orgs-row-${org.id}`}>
            <button
                type="button"
                onClick={onToggle}
                data-testid={`admin-orgs-row-toggle-${org.id}`}
                className="w-full text-left px-5 md:px-6 py-4 hover:bg-cream-light/60 transition-colors flex items-center justify-between gap-4"
            >
                <div className="flex items-center gap-3 min-w-0">
                    {org.brand?.logo_url ? (
                        <img src={org.brand.logo_url} alt={org.name} className="w-9 h-9 rounded-xl object-cover border border-cream-dark" />
                    ) : (
                        <div
                            className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-semibold"
                            style={{ backgroundColor: org.brand?.primary_color || "#1D8A85" }}
                        >
                            {(org.name || "?").trim().charAt(0).toUpperCase()}
                        </div>
                    )}
                    <div className="min-w-0">
                        <p className="font-medium text-[#1F2624] truncate">
                            {org.brand?.display_name || org.name}
                            {org.is_test && (
                                <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-2xl bg-yellow-light text-yellow-dark border border-yellow" data-testid={`admin-orgs-test-badge-${org.id}`}>
                                    <FlaskConical size={10} /> Test
                                </span>
                            )}
                        </p>
                        <p className="text-xs text-[#6E7A74] truncate">
                            <span className="font-mono">{org.code}</span> · {org.plan} · {org.member_count} member{org.member_count === 1 ? "" : "s"}
                            {org.contact_email && <> · {org.contact_email}</>}
                        </p>
                    </div>
                </div>
                <ExternalLink size={14} className={`text-[#6E7A74] transition-transform ${expanded ? "rotate-90" : ""}`} />
            </button>
            {expanded && <OrgBrandEditor org={org} onUpdated={onUpdated} />}
        </li>
    );
}

function OrgBrandEditor({ org, onUpdated }) {
    const [dangerBusy, setDangerBusy] = useState(false);
    const [brand, setBrand] = useState({
        display_name: org.brand?.display_name || "",
        tagline: org.brand?.tagline || "",
        primary_color: org.brand?.primary_color || "",
        accent_color: org.brand?.accent_color || "",
        logo_url: org.brand?.logo_url || "",
        public_landing_blurb: org.brand?.public_landing_blurb || "",
    });
    const [saving, setSaving] = useState(false);
    const [adminEmail, setAdminEmail] = useState("");
    const [addingAdmin, setAddingAdmin] = useState(false);
    const [resetEmail, setResetEmail] = useState("");
    const [resetBusy, setResetBusy] = useState(false);
    const [resetResult, setResetResult] = useState(null); // { email, temp_password }

    const save = async () => {
        setSaving(true);
        try {
            const payload = Object.fromEntries(Object.entries(brand).filter(([_, v]) => v && v.toString().trim()));
            await api.put(`/org/admin/${org.id}/brand`, payload);
            toast.success("Brand saved.");
            onUpdated?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save the brand.");
        } finally {
            setSaving(false);
        }
    };

    const addAdmin = async (e) => {
        e?.preventDefault?.();
        const em = adminEmail.trim().toLowerCase();
        if (!em || !em.includes("@")) { toast("Enter a valid email."); return; }
        setAddingAdmin(true);
        try {
            await api.post(`/org/admin/${org.id}/add-admin`, { user_email: em });
            toast.success(`${em} is now an org admin.`);
            setAdminEmail("");
            onUpdated?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't add that admin (user must already exist).");
        } finally {
            setAddingAdmin(false);
        }
    };

    const codeRedeemUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/?code=${org.code}#organizations`;

    return (
        <div className="px-5 md:px-6 pb-5 pt-1 bg-cream-light/40 border-t border-cream-dark" data-testid={`admin-orgs-editor-${org.id}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3">
                <Field label="Display name" testid={`row-${org.id}-display-name`} value={brand.display_name} onChange={v => setBrand(b => ({ ...b, display_name: v }))} placeholder={org.name} />
                <Field label="Tagline" testid={`row-${org.id}-tagline`} value={brand.tagline} onChange={v => setBrand(b => ({ ...b, tagline: v }))} />
                <ColorField label="Primary color" testid={`row-${org.id}-primary-color`} value={brand.primary_color} onChange={v => setBrand(b => ({ ...b, primary_color: v }))} />
                <ColorField label="Accent color" testid={`row-${org.id}-accent-color`} value={brand.accent_color} onChange={v => setBrand(b => ({ ...b, accent_color: v }))} />
                <Field label="Logo URL" testid={`row-${org.id}-logo-url`} value={brand.logo_url} onChange={v => setBrand(b => ({ ...b, logo_url: v }))} placeholder="https://example.com/logo.png" className="md:col-span-2" />
                <Field label="Landing blurb" testid={`row-${org.id}-blurb`} value={brand.public_landing_blurb} onChange={v => setBrand(b => ({ ...b, public_landing_blurb: v }))} className="md:col-span-2" />
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
                <button type="button" onClick={save} disabled={saving} data-testid={`admin-orgs-save-${org.id}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-teal-dark text-white text-sm font-medium hover:bg-teal disabled:opacity-60">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save brand
                </button>
                <a
                    href={codeRedeemUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`admin-orgs-preview-${org.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-2xl bg-white border border-cream-dark text-sm text-[#4C5551] hover:bg-cream-dark/40"
                >
                    <ExternalLink size={13} /> Preview redeem page
                </a>
            </div>

            {/* Invite additional org admin */}
            <form onSubmit={addAdmin} className="mt-5 pt-4 border-t border-cream-dark flex flex-wrap items-end gap-2" data-testid={`admin-orgs-invite-admin-${org.id}`}>
                <Field label="Invite another org admin (existing user email)" testid={`row-${org.id}-invite-admin`} type="email" value={adminEmail} onChange={setAdminEmail} placeholder="someone@partner.org" className="flex-1 min-w-[240px]" />
                <button type="submit" disabled={addingAdmin} className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-sage text-white text-sm font-medium hover:bg-sage-dark disabled:opacity-60">
                    {addingAdmin ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Add admin
                </button>
            </form>

            {/* Reset an org admin's password */}
            <div className="mt-5 pt-4 border-t border-cream-dark" data-testid={`admin-orgs-reset-pw-${org.id}`}>
                <p className="text-sm font-medium text-[#1F2624]">Reset an org admin&apos;s password</p>
                <p className="text-xs text-[#4C5551] mt-1">Generates a fresh temporary password for the org admin and returns it here so you can copy-paste it into a handoff. Overwrites the existing password.</p>
                <form
                    onSubmit={async (e) => {
                        e.preventDefault();
                        setResetBusy(true);
                        setResetResult(null);
                        try {
                            const body = resetEmail.trim() ? { admin_email: resetEmail.trim().toLowerCase() } : {};
                            const r = await api.post(`/org/admin/${org.id}/reset-password`, body);
                            setResetResult(r.data?.admin || null);
                            toast.success("Temporary password ready — copy it below.");
                        } catch (err) {
                            toast.error(err?.response?.data?.detail || "Couldn't reset that password.");
                        } finally {
                            setResetBusy(false);
                        }
                    }}
                    className="mt-2 flex flex-wrap items-end gap-2"
                >
                    <Field
                        label="Admin email (leave blank for the org's first admin)"
                        testid={`row-${org.id}-reset-email`}
                        type="email"
                        value={resetEmail}
                        onChange={setResetEmail}
                        placeholder="admin@partner.org"
                        className="flex-1 min-w-[240px]"
                    />
                    <button
                        type="submit"
                        disabled={resetBusy}
                        data-testid={`admin-orgs-reset-pw-submit-${org.id}`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-yellow-dark text-white text-sm font-medium hover:brightness-95 disabled:opacity-60"
                    >
                        {resetBusy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Reset password
                    </button>
                </form>
                {resetResult?.temp_password && (
                    <div
                        className="mt-3 rounded-2xl bg-yellow-light border border-yellow p-3 flex items-start gap-2"
                        data-testid={`admin-orgs-reset-pw-result-${org.id}`}
                    >
                        <KeyRound size={14} className="text-yellow-dark mt-0.5" />
                        <div className="text-sm min-w-0">
                            <p className="text-[#1F2624] font-medium truncate">New temp password for {resetResult.email}</p>
                            <p className="font-mono text-yellow-dark break-all">{resetResult.temp_password}</p>
                            <div className="flex flex-wrap gap-2 mt-1">
                                <button
                                    type="button"
                                    onClick={async () => {
                                        try {
                                            await navigator.clipboard.writeText(`${resetResult.email} / ${resetResult.temp_password}`);
                                            toast.success("Copied.");
                                        } catch { toast("Copy failed."); }
                                    }}
                                    className="inline-flex items-center gap-1.5 text-xs text-teal-dark hover:underline"
                                >
                                    <Copy size={12} /> Copy email + password
                                </button>
                            </div>
                            <p className="text-xs text-[#4C5551] mt-1">Won&apos;t be shown again once you leave this row.</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Test mode + delete */}
            <div className="mt-5 pt-4 border-t border-cream-dark" data-testid={`admin-orgs-danger-${org.id}`}>
                <p className="text-sm font-medium text-[#1F2624]">Test mode &amp; removal</p>
                <p className="text-xs text-[#4C5551] mt-1">
                    Test mode keeps the org (and every member account) out of Founder OS and investor numbers.
                    Deleting removes the org, its memberships, access grants, care plans, and contact logs — member accounts survive, just unlinked.
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                    <button
                        type="button"
                        disabled={dangerBusy}
                        data-testid={`admin-orgs-test-toggle-${org.id}`}
                        onClick={async () => {
                            setDangerBusy(true);
                            try {
                                const r = await api.put(`/org/admin/${org.id}/test-mode`, { is_test: !org.is_test });
                                toast.success(r.data.is_test
                                    ? `Marked as test — ${r.data.members_updated} member account(s) excluded from metrics.`
                                    : `Back to real — ${r.data.members_updated} member account(s) restored.`);
                                onUpdated?.();
                            } catch (err) {
                                toast.error(err?.response?.data?.detail || "Couldn't update test mode.");
                            } finally { setDangerBusy(false); }
                        }}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-medium disabled:opacity-60 ${org.is_test ? "bg-teal-dark text-white hover:bg-teal" : "bg-white border border-yellow text-yellow-dark hover:bg-yellow-light/40"}`}
                    >
                        {dangerBusy ? <Loader2 size={14} className="animate-spin" /> : <FlaskConical size={14} />}
                        {org.is_test ? "Unmark test — count as real" : "Mark as test org"}
                    </button>
                    <button
                        type="button"
                        disabled={dangerBusy}
                        data-testid={`admin-orgs-delete-${org.id}`}
                        onClick={async () => {
                            if (!window.confirm(`Delete "${org.name}" permanently? Members keep their accounts but lose the org connection. This cannot be undone.`)) return;
                            setDangerBusy(true);
                            try {
                                await api.delete(`/org/admin/${org.id}`);
                                toast.success(`${org.name} deleted.`);
                                onUpdated?.();
                            } catch (err) {
                                toast.error(err?.response?.data?.detail || "Couldn't delete the org.");
                            } finally { setDangerBusy(false); }
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-white border border-coral/40 text-coral-dark text-sm font-medium hover:bg-coral-light/30 disabled:opacity-60"
                    >
                        <Trash2 size={14} /> Delete organization
                    </button>
                </div>
            </div>
        </div>
    );
}



